import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { FormikProps } from 'formik';
import { ChangeEvent, useCallback, useMemo } from 'react';

import { fedexRedirect, gridDateTimeFormatter, openFile } from '@shared/commonFunctions';
import { dataGridCol, gridBooleanCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { FedexHistoryBox, TrackingNumberText } from '@styles/ContactManager/DealStyles';
import { colorTheme } from '@styles/muiTheme';

import { IContactData, IFedexLabel } from './interfaces';
import { getLabel, requestIncludeReadyToShip } from './request';

export interface ITracking {
	dateCreated: string;
	from: string;
	to: string;
	isReturnLabel: boolean;
	receviedDate: string;
	shippedDate: string;
	id: string;
	trackingNumber: string;
	isInBatch: boolean;
}

interface IFedexLabelProps extends FormikProps<IContactData> {
	data: IFedexLabel[];
	numericDealId: number;
	fedexTrackingData: ITracking[];
	dealId: string;
}

export const FedExLabelHistory = ({ fedexTrackingData, dealId, values, setFieldValue }: IFedexLabelProps) => {

	const onTrackingClick = useCallback(async (row: ITracking) => {
		const res = await getLabel(row.id, row.isInBatch, row.trackingNumber);
		if (res?.data) {
			openFile(res.data.document, 'pdf', res.data.fileName);
		}
	}, []);

  const sortedRows = useMemo(() => (fedexTrackingData || []).sort((a,b) => a.dateCreated > b.dateCreated ? -1 : 1) ,[fedexTrackingData]);

	const fedexTrackingCols: GridColDef[] = useMemo(() => [
		dataGridCol('dateCreated', {
			type: 'dateTime',
			valueFormatter: gridDateTimeFormatter,
		}),
		dataGridCol('shippedDate', {
			type: 'dateTime',
			valueFormatter: gridDateTimeFormatter,
		}),
		dataGridCol('receivedDate', {
			type: 'dateTime',
			valueFormatter: gridDateTimeFormatter,
		}),
		dataGridCol('inOut', {
			headerName: 'In/Out',
			flex: 0.5,
			minWidth: 80,
			valueGetter: ({ row }) => row.isReturnLabel ? 'In' : 'Out', 
			renderCell: ({row}) => <Box>{row.isReturnLabel ? 'In' : 'Out'}</Box>,
		}),
		dataGridCol('isReturnLabel', {
			flex: 1.2,
			minWidth: 100,
			...gridBooleanCell,
		}),
		dataGridCol('trackingNumber', {
			minWidth: 200,
			flex: 1.5,
			renderCell: (params) => (
				<TrackingNumberText
					onClick={() => fedexRedirect(params.value)}
				>
					{params.row.trackingNumber}
				</TrackingNumberText>
			),
		}),
		dataGridCol('from', {
			minWidth: 300,
			flex: 2,
		}),
		dataGridCol('to', {
			minWidth: 200,
			flex: 2,
			renderCell: (params) => (
				<TrackingNumberText
					onClick={() => onTrackingClick(params.row)}
				>
					{params.value || ''}
				</TrackingNumberText>
			)
		}),
	].map((field) => ({ ...field})), []);

	const handleCheckboxChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
		setFieldValue('includeInReadyToShip', event.target.checked);
		await requestIncludeReadyToShip(dealId, event.target.checked);
	}, [dealId]);

	return (
		<>
			<Typography variant="h2" my="20px" color={colorTheme.palette.primary.main} sx={{ fontSize: '20px', fontWeight: 600 }}>
				Fedex-Tracking
			</Typography>
			<FedexHistoryBox>
				<FormControlLabel
					name="includeInReadyToShip"
					label="Include in Ready to Ship Queue"
					control={
						<Checkbox
							checked={values.includeInReadyToShip}
							onChange={handleCheckboxChange}
						/>
					}
				/>
			</FedexHistoryBox>
      <PhoenixGrid
        autoHeight
        rows={sortedRows || []}
        pageSize={5}
        columns={fedexTrackingCols}
		getRowId={(row) => row?.trackingNumber + '-' + row?.id}
      />
		</>
	);
};
